import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { routeGuard } from '@/services/integrations/auth0';
import { RouteName, RoutePath } from '@/enums/Routes';
import { allUserRoles, UserRole } from '@/enums/UserRole';
import { ErrorCodes } from '@/enums/ErrorCodes';
import { routeGuard as salesForceRouteGuard } from '@/services/integrations/salesforce';
import {
  FeatureFlag,
  getFeatureFlag,
  PermissionType,
  getPermission,
} from '@/services/config';

const routes: Array<RouteRecordRaw> = [
  {
    path: RoutePath.BANK_VIEW,
    name: RouteName.BANK_VIEW,
    beforeEnter: routeGuard,
    meta: {
      priority: 1,
      showInMainNav: () => true,
      isEnabled: () => true,
      allowedRoles: [
        UserRole.SENIOR_MANAGER,
        UserRole.ADVISORY,
        UserRole.FINANCE,
        UserRole.TASK_APPROVER,
      ],
    },
    component: () =>
      import(
        /* webpackChunkName: "book-view" */ '../views/BookView/BookView.vue'
      ),
  },
  {
    path: RoutePath.BANKER_VIEW,
    name: RouteName.BANKER_VIEW,
    beforeEnter: routeGuard,
    meta: {
      priority: 1,
      showInMainNav: () => true,
      isEnabled: () => true,
      allowedRoles: [UserRole.BANKER],
    },
    component: () =>
      import(
        /* webpackChunkName: "book-view" */ '../views/BookView/BookView.vue'
      ),
  },
  {
    path: RoutePath.BANKER_VIEW_WITH_ID,
    name: RouteName.BANKER_VIEW_WITH_ID,
    beforeEnter: routeGuard,
    meta: {
      showInMainNav: () => false,
      isEnabled: () => true,
      allowedRoles: [
        UserRole.BANKER_ASSISTANT,
        UserRole.TEAM_HEAD,
        UserRole.MARKET_HEAD,
        UserRole.SENIOR_MANAGER,
        UserRole.ADVISORY,
        UserRole.FINANCE,
        UserRole.TASK_APPROVER,
      ],
    },
    component: () =>
      import(
        /* webpackChunkName: "book-view" */ '../views/BookView/BookView.vue'
      ),
  },
  {
    path: RoutePath.CLIENT_VIEW,
    name: RouteName.CLIENT_VIEW,
    beforeEnter: routeGuard,
    meta: {
      showInMainNav: () => false,
      isEnabled: () => true,
      allowedRoles: allUserRoles,
    },
    component: () =>
      import(
        /* webpackChunkName: "client-view" */ '../views/ClientView/ClientView.vue'
      ),
  },
  {
    path: RoutePath.QUICK_REJECT_VIEW,
    name: RouteName.QUICK_REJECT_VIEW,
    beforeEnter: routeGuard,
    meta: {
      showInMainNav: () => false,
      isEnabled: () => true,
      allowedRoles: allUserRoles,
    },
    component: () =>
      import(
        /* webpackChunkName: "quick-reject-view" */ '../views/QuickRejectView.vue'
      ),
  },
  {
    path: RoutePath.ERROR,
    name: RouteName.ERROR,
    meta: {
      showInMainNav: () => false,
      isEnabled: () => true,
      allowedRoles: allUserRoles,
    },
    component: () =>
      import(/* webpackChunkName: "error" */ '../views/Error.vue'),
  },
  {
    path: RoutePath.LOGGED_OUT,
    name: RouteName.LOGGED_OUT,
    meta: {
      priority: 10,
      showInMainNav: () => true,
      isEnabled: () => true,
      allowedRoles: allUserRoles,
    },
    component: () =>
      import(/* webpackChunkName: "logged-out" */ '../views/LoggedOut.vue'),
  },
  {
    path: RoutePath.MARKET_VIEW,
    name: RouteName.MARKET_VIEW,
    beforeEnter: routeGuard,
    meta: {
      priority: 1,
      showInMainNav: () => true,
      isEnabled: () => true,
      allowedRoles: [UserRole.MARKET_HEAD],
    },
    component: () =>
      import(
        /* webpackChunkName: "book-view" */ '../views/BookView/BookView.vue'
      ),
  },
  {
    path: RoutePath.MARKET_VIEW_WITH_ID,
    name: RouteName.MARKET_VIEW_WITH_ID,
    beforeEnter: routeGuard,
    meta: {
      showInMainNav: () => false,
      isEnabled: () => true,
      allowedRoles: [
        UserRole.SENIOR_MANAGER,
        UserRole.ADVISORY,
        UserRole.FINANCE,
        UserRole.TASK_APPROVER,
      ],
    },
    component: () =>
      import(
        /* webpackChunkName: "book-view" */ '../views/BookView/BookView.vue'
      ),
  },
  {
    path: RoutePath.NOT_FOUND,
    name: RouteName.NOT_FOUND,
    beforeEnter: routeGuard,
    props: {
      errorCode: ErrorCodes.NOT_FOUND,
    },
    meta: {
      showInMainNav: () => false,
      isEnabled: () => true,
      allowedRoles: allUserRoles,
    },
    component: () =>
      import(/* webpackChunkName: "error" */ '../views/Error.vue'),
  },
  {
    path: RoutePath.PROSPECT_VIEW,
    name: RouteName.PROSPECT_VIEW,
    beforeEnter: routeGuard,
    meta: {
      priority: 2,
      showInMainNav: () => true,
      isEnabled: () => getPermission(PermissionType.CAN_CREATE_PROSPECTS),
      allowedRoles: allUserRoles.filter(
        r => r !== UserRole.CREDIT_TEAM && r !== UserRole.INVESTMENT_SOLUTIONS
      ),
    },
    component: () =>
      import(
        /* webpackChunkName: "prospect-list" */ '../views/ProspectList.vue'
      ),
  },
  {
    path: RoutePath.ROOT,
    name: RouteName.ROOT,
    beforeEnter: routeGuard,
    meta: {
      showInMainNav: () => false,
      isEnabled: () => true,
      allowedRoles: allUserRoles,
    },
    component: () =>
      import(
        /* webpackChunkName: "role-redirection" */ '../views/RoleRedirection.vue'
      ),
  },
  {
    path: RoutePath.REDIRECT,
    name: RouteName.REDIRECT,
    beforeEnter: routeGuard,
    meta: {
      showInMainNav: () => false,
      isEnabled: () => true,
      allowedRoles: allUserRoles,
    },
    component: () =>
      import(/* webpackChunkName: "redirect" */ '../views/Redirect.vue'),
  },
  {
    path: RoutePath.TASK_LIST,
    name: RouteName.TASK_LIST,
    beforeEnter: routeGuard,
    meta: {
      priority: 3,
      showInMainNav: () => true,
      isEnabled: () => true,
      allowedRoles: [UserRole.BANKER],
      notificationCountKey: 'tasks',
    },
    component: () =>
      import(
        /* webpackChunkName: "task-list" */ '../views/taskView/TaskList.vue'
      ),
  },
  {
    path: RoutePath.TASK_LIST_ALL,
    name: RouteName.TASK_LIST_ALL,
    beforeEnter: routeGuard,
    meta: {
      priority: 4,
      showInMainNav: () => true,
      isEnabled: () => true,
      allowedRoles: [UserRole.BANKER],
      notificationCountKey: 'tasksAll',
    },
    component: () =>
      import(
        /* webpackChunkName: "task-list" */ '../views/taskView/TaskListAll.vue'
      ),
  },
  {
    path: RoutePath.TEAM_VIEW,
    name: RouteName.TEAM_VIEW,
    beforeEnter: routeGuard,
    meta: {
      priority: 1,
      showInMainNav: () => true,
      isEnabled: () => true,
      allowedRoles: [UserRole.TEAM_HEAD],
    },
    component: () =>
      import(
        /* webpackChunkName: "book-view" */ '../views/BookView/BookView.vue'
      ),
  },
  {
    path: RoutePath.TEAM_VIEW_WITH_ID,
    name: RouteName.TEAM_VIEW_WITH_ID,
    beforeEnter: routeGuard,
    meta: {
      showInMainNav: () => false,
      isEnabled: () => true,
      allowedRoles: [
        UserRole.MARKET_HEAD,
        UserRole.SENIOR_MANAGER,
        UserRole.ADVISORY,
        UserRole.FINANCE,
        UserRole.BANKER_ASSISTANT,
      ],
    },
    component: () =>
      import(
        /* webpackChunkName: "book-view" */ '../views/BookView/BookView.vue'
      ),
  },
  {
    path: RoutePath.SALESFORCE_CALLBACK,
    name: RouteName.SALESFORCE_CALLBACK,
    beforeEnter: salesForceRouteGuard,
    component: () =>
      import(
        /* webpackChunkName: "salesforce-callback" */ '../views/SalesforceConnection.vue'
      ),
  },
  {
    path: RoutePath.BONUS_CALCULATOR,
    name: RouteName.BONUS_CALCULATOR,
    beforeEnter: routeGuard,
    meta: {
      priority: 6,
      showInMainNav: () => true,
      isEnabled: () => getFeatureFlag(FeatureFlag.BONUS_CALCULATOR).enabled,
      allowedRoles: [UserRole.BANKER, UserRole.TEAM_HEAD],
    },
    component: () =>
      import(
        /* webpackChunkName: "bonus-calculator" */ '../views/bonusCalculator/BonusCalculator.vue'
      ),
  },
  {
    path: RoutePath.LEAD_OVERVIEW,
    name: RouteName.LEAD_OVERVIEW,
    beforeEnter: routeGuard,
    meta: {
      priority: 5,
      showInMainNav: () => true,
      isEnabled: () => getFeatureFlag(FeatureFlag.LEADS).enabled,
      allowedRoles: [UserRole.BANKER, UserRole.OUTBOUND],
      notificationCountKey: 'leads',
    },
    component: () =>
      import(
        /* webpackChunkName: "lead-overview" */ '../views/LeadOverview/LeadOverview.vue'
      ),
  },
  {
    path: RoutePath.SALES_PIPELINE,
    name: RouteName.SALES_PIPELINE,
    beforeEnter: routeGuard,
    meta: {
      priority: 7,
      showInMainNav: () => true,
      isEnabled: () => getFeatureFlag(FeatureFlag.SALES_PIPELINE).enabled,
      allowedRoles: [UserRole.BANKER],
    },
    component: () =>
      import(
        /* webpackChunkName: "lead-overview" */ '../views/SalesPipelineView/SalesPipelineView.vue'
      ),
  },
  {
    path: RoutePath.INDIVIDUAL_LEAD,
    name: RouteName.INDIVIDUAL_LEAD,
    beforeEnter: routeGuard,
    meta: {
      showInMainNav: () => false,
      isEnabled: () => true,
      allowedRoles: [UserRole.BANKER, UserRole.OUTBOUND],
    },
    component: () =>
      import(
        /* webpackChunkName: "individual-lead" */ '../views/LeadOverview/IndividualLead.vue'
      ),
  },
  {
    path: RoutePath.X_PLAN_LEAD,
    name: RouteName.X_PLAN_LEAD,
    beforeEnter: (to, _, next) => {
      const externalUrl = `https://cgwm.xplan.iress.co.uk/client_hub/entity/${to.params.id}`;
      window.location.href = externalUrl;
      next();
    },
    // eslint-disable-next-line
    component: { render() {} },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // always scroll to top on route change
    return { top: 0 };
  },
});

export const checkViewIsEnabled = async () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentRoute = router.currentRoute as any;
  if (!currentRoute.value.meta.isEnabled()) {
    router.push({
      name: RouteName.ERROR,
      params: {
        errorCode: ErrorCodes.NOT_AUTHORISED,
      },
    });
    return false;
  }

  return true;
};

export default router;
